import { View, Text, Platform, ActivityIndicator, type ViewStyle } from 'react-native'
import { Pressable } from '../ui/Pressable'
import { useTranslation } from 'react-i18next'
import { Smartphone } from '@/components/ui/icons'
import { GoogleIcon } from './GoogleIcon'
import { AppleIcon } from './AppleIcon'
import { env } from '../../lib/env'
import { useSemanticColors } from '../../lib/theme/tokens'
import { FONTS, SPACING, RADIUS } from '../../lib/brand'

/**
 * Web Apple sign-in is built but not yet switchable on.
 *
 * The service handles `Platform.OS === 'web'` through Apple's hosted flow, but
 * that flow needs two things that only exist in Apple's console and Supabase:
 * a Service ID — a SECOND client id, which Apple refuses to let equal the
 * bundle — carrying app.tago.rent and the Supabase callback, and a Sign in
 * with Apple key whose generated secret Supabase holds.
 *
 * Flip this to `true` in the SAME commit that lands both. A sign-in button
 * that always fails costs more trust than a missing one.
 *
 * Deliberately a constant rather than an EXPO_PUBLIC_ env var: the repo
 * requires a new env var to be created in EAS, added to lib/env.ts and wired
 * into eas-build-pre-install.sh together, which is a lot of moving parts for a
 * switch that gets thrown exactly once.
 */
const APPLE_WEB_AUTH_READY = true

// Apple requires Sign in with Apple wherever a third-party login is offered
// (App Review 4.8), so this follows Google's surfaces rather than iOS alone.
const showApple = Platform.OS === 'ios' || (Platform.OS === 'web' && APPLE_WEB_AUTH_READY)

/**
 * Phone entry is ON only while a channel can actually deliver a code.
 *
 * This constant tracks ONE question: can a code be delivered right now? It has
 * been false twice, both times because the provider had run out of balance — a
 * phone button that sends a code which never arrives is worse than no button,
 * since it strands the most culturally-default path on the first screen.
 *
 * Codes now go out over WhatsApp through Verifik (`send-phone-otp`). An
 * exhausted balance there answers 403 `insufficient_credits`, which the server
 * turns into a 503 for the person and a CRITICAL log line plus a Sentry event
 * for us — so the condition that used to be discovered by a stranger failing to
 * sign up is now something we are told about. Flip this to false in the same
 * change that acknowledges such an alert; do not leave the door open over a
 * dead channel.
 *
 * Kept as a code constant (not the EXPO_PUBLIC_PHONE_AUTH_ENABLED env var) on
 * purpose: this flips through an OTA JS update to already-installed apps, and
 * it leaves the env/eas wiring and its validators untouched.
 */
const PHONE_AUTH_ENTRY_ENABLED = false

interface SocialButtonProps {
  icon: React.ReactNode
  label: string
  onPress: () => void
  loading?: boolean
  isDark: boolean
  a11yLabel: string
  a11yHint: string
  testID?: string
}

const webCursor =
  Platform.OS === 'web'
    ? ({ cursor: 'pointer' } as ViewStyle & { cursor?: string })
    : ({} as ViewStyle)

function SocialButton({
  icon,
  label,
  onPress,
  loading = false,
  isDark,
  a11yLabel,
  a11yHint,
  testID,
}: SocialButtonProps) {
  const colors = useSemanticColors(isDark)
  const backgroundColor = isDark ? colors.surfaceSubtle : colors.surface
  const borderColor = isDark ? colors.border : colors.textDisabled
  return (
    <Pressable
      onPress={onPress}
      disabled={loading}
      style={({ pressed }) => ({
        flex: 1,
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        gap: SPACING.sm,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor,
        backgroundColor,
        paddingVertical: SPACING.md,
        minHeight: 56,
        opacity: pressed ? 0.7 : 1,
        ...webCursor,
      })}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      accessibilityHint={a11yHint}
      accessibilityState={{ disabled: loading, busy: loading }}
    >
      {loading ? (
        <ActivityIndicator size="small" color={colors.primary} />
      ) : (
        <>
          {icon}
          <Text
            style={{
              fontFamily: FONTS.body,

              fontSize: 15,
              color: colors.textPrimary,
            }}
          >
            {label}
          </Text>
        </>
      )}
    </Pressable>
  )
}

interface SocialAuthRowProps {
  onGoogle: () => void
  onApple: () => void
  onPhone: () => void
  googleLoading?: boolean
  appleLoading?: boolean
  isDark?: boolean
  testIDPrefix?: string
  phoneEnabled?: boolean
}

export function SocialAuthRow({
  onGoogle,
  onApple,
  onPhone,
  googleLoading = false,
  appleLoading = false,
  isDark = false,
  testIDPrefix,
  phoneEnabled = env.isPhoneAuthEnabled && PHONE_AUTH_ENTRY_ENABLED,
}: SocialAuthRowProps) {
  const { t } = useTranslation()
  const colors = useSemanticColors(isDark)
  const appleColor = isDark ? '#FFFFFF' : '#000000'
  const phoneColor = colors.textPrimary

  return (
    <View style={{ flexDirection: 'row', gap: 12 }}>
      <SocialButton
        icon={<GoogleIcon size={18} />}
        label={t('welcome.auth_google')}
        onPress={onGoogle}
        loading={googleLoading}
        isDark={isDark}
        a11yLabel={t('welcome.auth_google')}
        a11yHint={t('a11y.welcome_google_hint')}
        testID={testIDPrefix ? `${testIDPrefix}.google-button` : undefined}
      />
      {showApple && (
        <SocialButton
          icon={<AppleIcon size={18} color={appleColor} />}
          label={t('welcome.auth_apple')}
          onPress={onApple}
          loading={appleLoading}
          isDark={isDark}
          a11yLabel={t('welcome.auth_apple')}
          a11yHint={t('a11y.welcome_apple_hint')}
          testID={testIDPrefix ? `${testIDPrefix}.apple-button` : undefined}
        />
      )}
      {phoneEnabled ? (
        <SocialButton
          icon={<Smartphone size={18} color={phoneColor} />}
          label={t('welcome.auth_phone')}
          onPress={onPhone}
          isDark={isDark}
          a11yLabel={t('welcome.auth_phone')}
          a11yHint={t('a11y.welcome_phone_hint')}
          testID={testIDPrefix ? `${testIDPrefix}.phone-button` : undefined}
        />
      ) : null}
    </View>
  )
}
